import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Download, Palette, Type, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Seo, withTemplate, canonicalize, defaultSite } from '@/lib/seo';
import { fireEvent } from '@/lib/analytics';
import logoImg from '@/assets/logo.png';

export default function Press() {
  useEffect(() => {
    fireEvent('press_view', {});
  }, []);

  const brandColors = [
    { name: 'Primary', hex: '#8B5CF6', hsl: '258 90% 66%' },
    { name: 'Secondary', hex: '#D946EF', hsl: '292 84% 61%' },
    { name: 'Accent', hex: '#0EA5E9', hsl: '199 89% 48%' },
  ];

  const logoAssets = [{ name: 'Attain Domains Logo', path: logoImg, format: 'PNG' }];

  return (
    <>
      <Seo
        title={withTemplate('Press Kit & Brand Assets')}
        description="Download Attain Domains brand assets, logos, and brand guidelines for press and marketing use."
        canonical={canonicalize('/press')}
        og={{
          title: 'Press Kit & Brand Assets',
          description: 'Official brand assets and guidelines for Attain Domains',
          url: canonicalize('/press'),
          type: 'website',
          image: `${defaultSite.baseUrl}/favicons/favicon-32.png`,
        }}
        twitter={{
          card: 'summary',
          title: 'Press Kit & Brand Assets',
          description: 'Official brand assets and guidelines for Attain Domains',
          image: `${defaultSite.baseUrl}/favicons/favicon-32.png`,
        }}
      />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/10">
        <div className="container mx-auto px-4 py-16 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
              Press Kit
            </h1>
            <p className="text-2xl text-muted-foreground mb-8 leading-relaxed max-w-3xl mx-auto">
              Brand assets, logos, and usage guidelines for press, partners, and affiliates.
            </p>
          </div>

          {/* Brand Overview */}
          <Card className="mb-12 shadow-lg border-border/50">
            <CardContent className="p-8">
              <h2 className="text-3xl font-bold mb-6">About Attain Domains</h2>
              <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
                <p>
                  <strong className="text-foreground">Company:</strong> Attain Domains
                </p>
                <p>
                  <strong className="text-foreground">Founded:</strong> 2024
                </p>
                <p>
                  <strong className="text-foreground">Mission:</strong> Help startups secure
                  brandable domains with guided, secure transfers and practical launch support.
                </p>
                <p>
                  <strong className="text-foreground">Founder:</strong> Adam (Product-minded builder
                  focused on clarity and momentum)
                </p>
                <p>
                  <strong className="text-foreground">Key Differentiator:</strong> Direct founder
                  access, payment protection with fee coverage, and
                  complimentary consulting for domain buyers.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Logo Assets */}
          <Card className="mb-12 shadow-lg border-border/50">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <ImageIcon className="h-6 w-6 text-primary" />
                <h2 className="text-3xl font-bold">Logo Assets</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {logoAssets.map((asset, index) => (
                  <Card key={index} className="bg-muted/30">
                    <CardContent className="p-6">
                      <div className="bg-white rounded-lg p-8 mb-4 flex items-center justify-center min-h-[200px]">
                        <img src={asset.path} alt={asset.name} className="max-h-32 w-auto" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-foreground">{asset.name}</h3>
                          <p className="text-sm text-muted-foreground">{asset.format}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = asset.path;
                            link.download = `${asset.name.toLowerCase().replace(/\s+/g, '-')}.png`;
                            link.click();
                          }}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Favicon Set */}
          <Card className="mb-12 shadow-lg border-border/50">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <ImageIcon className="h-6 w-6 text-primary" />
                <h2 className="text-3xl font-bold">Favicon Set</h2>
              </div>
              <p className="text-muted-foreground mb-6">
                Available in multiple sizes for web and app use:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { size: '16x16', path: '/favicons/favicon-16.png' },
                  { size: '32x32', path: '/favicons/favicon-32.png' },
                  { size: '192x192', path: '/favicons/android-chrome-192x192.png' },
                  { size: '512x512', path: '/favicons/android-chrome-512x512.png' },
                ].map((favicon) => (
                  <Card key={favicon.size} className="bg-muted/30">
                    <CardContent className="p-4 text-center">
                      <div className="bg-white rounded-lg p-4 mb-3 flex items-center justify-center">
                        <img
                          src={favicon.path}
                          alt={`Favicon ${favicon.size}`}
                          className="w-12 h-12"
                        />
                      </div>
                      <p className="text-sm font-medium text-foreground mb-2">{favicon.size}</p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = favicon.path;
                          link.download = `favicon-${favicon.size}.png`;
                          link.click();
                        }}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Brand Colors */}
          <Card className="mb-12 shadow-lg border-border/50">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Palette className="h-6 w-6 text-primary" />
                <h2 className="text-3xl font-bold">Brand Colors</h2>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {brandColors.map((color, index) => (
                  <Card key={index} className="bg-muted/30">
                    <CardContent className="p-6">
                      <div
                        className="w-full h-32 rounded-lg mb-4 shadow-md"
                        style={{ backgroundColor: color.hex }}
                      ></div>
                      <h3 className="font-semibold text-foreground mb-2">{color.name}</h3>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <p>
                          <strong>HEX:</strong> {color.hex}
                        </p>
                        <p>
                          <strong>HSL:</strong> {color.hsl}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Typography */}
          <Card className="mb-12 shadow-lg border-border/50">
            <CardContent className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <Type className="h-6 w-6 text-primary" />
                <h2 className="text-3xl font-bold">Typography</h2>
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2">Primary Font</h3>
                  <p className="text-muted-foreground mb-4">
                    System font stack for optimal performance and readability
                  </p>
                  <div className="bg-muted/30 p-6 rounded-lg border border-border/30">
                    <p className="font-sans text-2xl mb-2">
                      The quick brown fox jumps over the lazy dog
                    </p>
                    <p className="text-sm text-muted-foreground">
                      -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Headings</h3>
                  <div className="bg-muted/30 p-6 rounded-lg border border-border/30 space-y-2">
                    <h1 className="text-4xl font-bold">Heading 1 - Bold</h1>
                    <h2 className="text-3xl font-bold">Heading 2 - Bold</h2>
                    <h3 className="text-2xl font-semibold">Heading 3 - Semibold</h3>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Usage Guidelines */}
          <Card className="mb-12 shadow-lg border-border/50 bg-gradient-to-br from-primary/5 to-secondary/5">
            <CardContent className="p-8">
              <h2 className="text-3xl font-bold mb-6">Usage Guidelines</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">✓ Do</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Use the logo with adequate spacing and clear background</li>
                    <li>• Maintain the original aspect ratio</li>
                    <li>• Use brand colors consistently</li>
                    <li>• Link back to attaindomains.com when featuring our brand</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground">✗ Don't</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• Alter, distort, or recolor the logo</li>
                    <li>• Place logo on busy or low-contrast backgrounds</li>
                    <li>• Imply endorsement without written permission</li>
                    <li>• Use outdated or unofficial brand assets</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="shadow-lg border-border/50">
            <CardContent className="p-8 text-center">
              <h2 className="text-3xl font-bold mb-4">Press Inquiries</h2>
              <p className="text-lg text-muted-foreground mb-6">
                For press inquiries, interviews, or additional assets, please contact me.
              </p>
              <Button size="lg" asChild>
                <a href="/support/contact">Contact Press Team</a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
